import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import searchService from '../../services/searchService';
import { rankSections } from '../../utils/searchRank';
import useUserLocation from '../../hooks/useUserLocation';

const LOCATION_KEY = 'pr.userLocation';

export function readLocation() {
  try {
    const raw = localStorage.getItem(LOCATION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

const SCOPED = {
  events: (q, take) => searchService.searchEvents({ q, take }),
  people: (q, take) => searchService.searchPeople({ q, take }),
  clubs: (q, take) => searchService.searchClubs({ q, take }),
};
const LABELS = { events: 'Events', people: 'People', clubs: 'Clubs' };
/** A single scope lists up to this many (top-header handoff §4). */
export const SCOPED_TAKE = 12;

/**
 * Search state for the top header's panel (top-header handoff §4; data layer from
 * docs/plans/2026-08-handoff-layout/08-search.md).
 *
 * - `all` scope: the combined `/search` payload (5 per category server-side). The browse
 *   (empty-query) payload is fetched once and cached for the session; typing filters the cached
 *   payload instantly (so the list never blanks) while a debounced request (150ms) fetches the
 *   authoritative one. The per-scope counts come from it.
 * - `events` / `people` / `clubs`: that category's paged endpoint, up to 12 rows.
 * - `rows` is the flat, keyboard-navigable list: the city row (all scope) then every item.
 */
let browseCache = null;
export function resetSearchCache() { browseCache = null; }

export default function useSearch(open, { scope = 'all', perSection = 4 } = {}) {
  const [query, setQuery] = useState('');
  const [payload, setPayload] = useState(browseCache);
  const [scoped, setScoped] = useState(null); // { key, q, items, total }
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const reqId = useRef(0);
  const scopedId = useRef(0);

  // The city the header's location control is set to — the same store, so COMING UP NEAR says
  // the town that is actually being browsed and follows a change without a reload.
  const browse = useUserLocation();
  const location = useMemo(
    () => (browse.lat != null ? { lat: browse.lat, lng: browse.lng, label: browse.city } : { ...(readLocation() || {}), label: browse.city }),
    [browse.lat, browse.lng, browse.city],
  );

  const fetchFor = useCallback(async (q) => {
    const id = ++reqId.current;
    setLoading(true);
    const r = await searchService.search({ q, lat: location?.lat ?? null, lng: location?.lng ?? null });
    if (id !== reqId.current) return; // a newer keystroke won
    if (r?.success) {
      setPayload(r.data);
      if (!q) browseCache = r.data; // browse payload is stable for the session
    }
    setLoading(false);
  }, [location]);

  // Browse payload on first open.
  useEffect(() => {
    if (!open) return;
    if (browseCache) { setPayload(browseCache); return; }
    fetchFor('');
  }, [open, fetchFor]);

  // Debounced live search (the combined payload also feeds the scope counts).
  useEffect(() => {
    if (!open) return undefined;
    const q = query.trim();
    if (!q) {
      if (browseCache) setPayload(browseCache);
      return undefined;
    }
    const t = setTimeout(() => fetchFor(q), 150);
    return () => clearTimeout(t);
  }, [query, open, fetchFor]);

  // One scope: its own paged endpoint.
  useEffect(() => {
    if (!open || scope === 'all' || !SCOPED[scope]) return undefined;
    const q = query.trim();
    const t = setTimeout(async () => {
      const id = ++scopedId.current;
      const r = await SCOPED[scope](q, SCOPED_TAKE);
      if (id !== scopedId.current) return;
      if (r?.success) setScoped({ key: scope, q, items: r.data?.items || [], total: r.data?.total || 0 });
    }, q ? 150 : 0);
    return () => clearTimeout(t);
  }, [open, scope, query]);

  const term = query.trim();

  const sections = useMemo(() => {
    if (scope === 'all') return rankSections(payload, term, perSection);
    // Until the scoped request lands, narrow what the combined payload already holds.
    const fresh = scoped && scoped.key === scope && scoped.q === term;
    const fallback = rankSections(payload, term, SCOPED_TAKE).find((s) => s.key === scope);
    const items = fresh ? scoped.items : (fallback?.items || []);
    const total = fresh ? scoped.total : (fallback?.serverTotal || 0);
    return items.length ? [{ key: scope, label: LABELS[scope], items, serverTotal: total, total }] : [];
  }, [scope, payload, scoped, term, perSection]);

  // A query that names a place, e.g. "kelowna". Everything below is already
  // scoped to it server-side; this is the way through to the whole city.
  const city = term && scope === 'all' ? payload?.city || null : null;

  const rows = useMemo(() => {
    const out = [];
    if (city) out.push({ type: 'city', city });
    sections.forEach((s) => s.items.forEach((item) => out.push({ type: 'item', section: s.key, item })));
    return out;
  }, [sections, city]);

  // Reset the highlight whenever the visible set changes.
  useEffect(() => { setHighlight(0); }, [query, payload, scope]);

  const counts = {
    events: payload?.events?.total ?? 0,
    people: payload?.people?.total ?? 0,
    clubs: payload?.clubs?.total ?? 0,
  };

  return {
    query, setQuery,
    sections, rows, city, counts,
    browse: browseCache || (!term ? payload : null),
    loading,
    highlight, setHighlight,
    term,
    location,
    isEmpty: !loading && sections.length === 0 && !city,
  };
}
